/**
 * 画面共通の対象月(YYYY-MM)。トップバーの月セレクタで切り替え、各画面がこれを見る。
 * 週次表示はフェーズ2で追加する。
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { currentMonth } from '@core/lib/date';
import type { MonthStr } from '@core/lib/types';

type PeriodContextValue = {
  month: MonthStr;
  setMonth: (m: MonthStr) => void;
  /** セレクタに出す選択肢(過去18か月〜翌3か月) */
  options: MonthStr[];
};

const PeriodContext = createContext<PeriodContextValue | null>(null);

function buildOptions(base: MonthStr): MonthStr[] {
  const [y, m] = base.split('-').map(Number) as [number, number];
  const list: MonthStr[] = [];
  for (let offset = -18; offset <= 3; offset++) {
    const d = new Date(Date.UTC(y, m - 1 + offset, 1));
    list.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return list.reverse();
}

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [month, setMonth] = useState<MonthStr>(currentMonth());
  const value = useMemo<PeriodContextValue>(
    () => ({ month, setMonth, options: buildOptions(currentMonth()) }),
    [month],
  );
  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error('usePeriod は PeriodProvider の内側で使ってください');
  return ctx;
}
